import ReceiptStrategy from './ReceiptStrategy';
import ReceivablePaymentForm from '../components/ReceivablePaymentForm';
import { registerReceivablePayment } from '../../../../usecases/accounts-management-usecase';
import { formatMoney } from '../../../../utils/formating';
import { lang } from '../../../../utils/langs';

/**
 * Estrategia para registrar abonos a cuentas por cobrar.
 * 
 * @extends ReceiptStrategy
 */
class ReceivablePaymentStrategy extends ReceiptStrategy {
    /**
     * Retorna el componente de formulario para abonos a CXC.
     * 
     * @returns {Function} Componente ReceivablePaymentForm
     */
    getFormComponent() {
        return ReceivablePaymentForm;
    }

    /**
     * Valida los datos del formulario de abono a CXC.
     * 
     * @param {Object} data - Datos del formulario
     * @param {number} data.account_id - ID de la cuenta
     * @param {number} data.amount - Monto a abonar
     * @param {number} data.current_balance - Saldo actual de la cuenta
     * @param {number} data.paymethod_id - ID del método de pago
     * @returns {Object} { isValid: boolean, errors: Object }
     */
    validate(data) {
        const errors = {};

        // Validar cuenta seleccionada
        if (!data.account_id) {
            errors.account_id = 'Debe seleccionar una cuenta';
        }

        // Validar monto
        if (!data.amount || parseFloat(data.amount) <= 0) {
            errors.amount = 'El monto debe ser mayor a 0';
        } else if (parseFloat(data.amount) > parseFloat(data.current_balance)) {
            errors.amount = 'El monto no puede exceder el saldo actual';
        }

        // Validar método de pago
        if (!data.paymethod_id) {
            errors.paymethod_id = 'Debe seleccionar un método de pago';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    /**
     * Envía el abono a CXC al backend.
     * 
     * @param {Object} data - Datos del abono
     * @param {Object} context - Contexto con uid, cid, profile
     * @returns {Promise<Object>} Respuesta del servidor con datos del abono
     */
    async submit(data, context) {
        const { uid, cid, profile } = context;

        const payload = {
            amount: parseFloat(data.amount),
            paymethod_id: parseInt(data.paymethod_id),
            reference: data.reference || null
        };

        const response = await registerReceivablePayment(
            uid,
            cid,
            data.account_id,
            payload
        );

        // Retornar datos combinados para impresión
        return {
            ...response,
            third: {
                id: data.third_id,
                name: data.third_name,
                identification: data.third_identification
            },
            previous_balance: data.current_balance,
            payment_amount: parseFloat(data.amount),
            new_balance: data.current_balance - parseFloat(data.amount),
            payment_method: data.paymethod_name,
            reference: data.reference
        };
    }

    /**
     * Genera el contenido HTML para impresión de comprobante de abono CXC.
     * 
     * @param {Object} data - Datos del abono
     * @param {Object} profile - Datos de la empresa
     * @returns {string} HTML para imprimir
     */
    generatePrintContent(data, profile) {
        const { third, previous_balance, payment_amount, new_balance, reference } = data;

        return `
            <div id="print-content" style="
                width: 100%;
                min-width: 58mm;
                max-width: 80mm;
                font-family: 'Courier New', monospace;
                font-size: 11px;
                padding: 8px;
                color: #000;
                margin: 0 auto;
            ">
                <div style="text-align: center;">
                    <strong>${profile?.companyName || 'Empresa'}</strong><br/>
                    ${profile?.email || ''}<br/>
                    ${profile?.phone || ''}<br/>
                    ${profile?.address || ''}<br/>
                    -------------------------------
                </div>
                
                <div style="text-align: center; margin: 8px 0;">
                    <strong>COMPROBANTE DE ABONO</strong><br/>
                    <strong>CUENTA POR COBRAR</strong>
                </div>
                
                <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
                
                <div style="margin-top: 8px;">
                    <div><strong>Cliente:</strong> ${third?.name || 'N/A'}</div>
                    <div><strong>ID:</strong> ${third?.identification || 'N/A'}</div>
                    <div><strong>Fecha:</strong> ${new Date().toLocaleString()}</div>
                </div>
                
                <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
                
                <table style="width: 100%;">
                    <tr>
                        <td>Saldo Anterior:</td>
                        <td style="text-align: right;">${formatMoney(previous_balance)}</td>
                    </tr>
                    <tr>
                        <td><strong>Abono:</strong></td>
                        <td style="text-align: right;"><strong>${formatMoney(payment_amount)}</strong></td>
                    </tr>
                </table>
                
                <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
                
                <table style="width: 100%;">
                    <tr>
                        <td><strong>Nuevo Saldo:</strong></td>
                        <td style="text-align: right;"><strong>${formatMoney(new_balance)}</strong></td>
                    </tr>
                </table>
                
                <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
                
                <div style="margin-top: 8px;">
                    <div><strong>Método de Pago:</strong> ${data.payment_method_name || 'N/A'}</div>
                    ${reference ? `<div><strong>Referencia:</strong> ${reference}</div>` : ''}
                </div>
                
                ${new_balance === 0 ? `
                    <div style="text-align: center; margin: 10px 0; padding: 5px; background-color: #e8f5e9; border: 1px solid #4caf50;">
                        <strong>✓ CUENTA SALDADA</strong>
                    </div>
                ` : ''}
                
                <div style="text-align: center; margin-top: 10px;">
                    ¡Gracias por tu preferencia!
                </div>
            </div>
        `;
    }

    /**
     * Retorna el estado inicial del formulario de abono a CXC.
     * 
     * @returns {Object} Estado inicial
     */
    getInitialState() {
        return {
            account_id: null,
            amount: '',
            paymethod_id: '',
            reference: '',
            current_balance: 0,
            third_name: '',
            third_id: null,
            third_identification: ''
        };
    }

    /**
     * Retorna el título del botón de envío.
     * 
     * @returns {string} Título del botón
     */
    getSubmitButtonTitle() {
        return 'Registrar Abono';
    }

    /**
     * Retorna el nombre del evento a disparar después de crear.
     * 
     * @returns {string} Nombre del evento
     */
    getUpdateEventName() {
        return 'update-accounts';
    }
}

export default ReceivablePaymentStrategy;
